import React from 'react'
import { Table } from 'react-bootstrap'
import ScreenshotPlaceholder from '../../../components/ScreenshotPlaceholder'
import { Tip, Warn, Code, ArticleLink, Ext, TemplateBtn } from '../_helpers'

export const SECTION_ARTICLES = [
  {
    id: 'tb-upload-errors',
    category: 'troubleshooting',
    title: 'Warum ist mein Upload fehlgeschlagen? — Fehlermeldungen erklärt',
    summary: 'Jeder rote Fehler und jede gelbe Warnung mit Ursache und Lösung.',
    body: (
      <>
        <p>
          Wird ein Upload abgelehnt, erscheint ein <strong>roter</strong> Banner mit einer Liste der
          Probleme. <strong>Gelbe</strong> Meldungen sind nur Warnungen — die Datei wird trotzdem
          akzeptiert. Suchen Sie Ihre Meldung unten heraus.
        </p>

        <h5 className="mt-3">Rote Fehler (Datei abgelehnt)</h5>
        <Table bordered size="sm" responsive className="small">
          <thead style={{ background: 'var(--color-neutral-100)' }}>
            <tr><th>Meldung</th><th>Bedeutung &amp; Lösung</th></tr>
          </thead>
          <tbody>
            <tr>
              <td><Code>Missing required column: "..."</Code></td>
              <td>Die Kopfzeile enthält keine Spalte, die die Datei benötigt — meistens ein
                Rechtschreib- oder Groß-/Kleinschreibungsfehler. Überschriften müssen exakt
                übereinstimmen, z. B. <Code>consumption_kwh</Code>, vollständig in Kleinbuchstaben.
                Korrigieren Sie die erste Zeile und laden Sie die Datei erneut hoch.</td>
            </tr>
            <tr>
              <td><Code>Value must be a number, got: "..."</Code></td>
              <td>Eine Zelle, die eine Zahl enthalten soll, enthält etwas anderes — fast immer ein
                <strong> Dezimalkomma</strong> (<Code>3,5</Code>); es muss ein Punkt sein (<Code>3.5</Code>).
                Um eine ganze Datei auf einmal zu konvertieren, lesen Sie
                <ArticleLink to="data-fix-commas"> Eine Datei mit Kommas als Dezimaltrennzeichen
                korrigieren</ArticleLink>. Entfernen Sie außerdem Einheiten, Leerzeichen oder anderen
                Text aus Zahlenzellen.</td>
            </tr>
            <tr>
              <td><Code>Value must be non-negative, got: ...</Code></td>
              <td>Ein Verbrauchs-, Erzeugungs- oder Preiswert ist negativ. Diese Werte dürfen nicht
                unter null liegen — eine negative Zahl bedeutet meist, dass zwei Spalten vertauscht
                wurden.</td>
            </tr>
            <tr>
              <td><Code>Invalid timestamp format: "..."</Code></td>
              <td>Eine Datums-/Uhrzeitzelle konnte nicht gelesen werden. Verwenden Sie
                <Code>2025-01-01T14:00:00Z</Code>. Siehe
                <ArticleLink to="data-csv-basics"> CSV-Dateien korrekt erstellen</ArticleLink> für
                die akzeptierten Datumsformate und eine Formel, die eine ganze Spalte auf einmal
                korrigiert.</td>
            </tr>
            <tr>
              <td><Code>CSV file contains no data rows</Code></td>
              <td>Die Datei enthält nur eine Kopfzeile oder ist leer. Fügen Sie Ihre stündlichen
                Datenzeilen hinzu.</td>
            </tr>
            <tr>
              <td><Code>period_start must be before period_end</Code></td>
              <td>(Energieabrechnungsdateien) Das Startdatum einer Abrechnung ist gleich dem oder
                liegt nach dem Enddatum. Korrigieren Sie die Datumsangaben.</td>
            </tr>
          </tbody>
        </Table>

        <h5 className="mt-4">Gelbe Warnungen (Datei wird trotzdem akzeptiert)</h5>
        <Table bordered size="sm" responsive className="small">
          <thead style={{ background: 'var(--color-neutral-100)' }}>
            <tr><th>Meldung</th><th>Bedeutung</th></tr>
          </thead>
          <tbody>
            <tr><td><Code>Extra columns found and will be ignored</Code></td><td>Ihre Datei enthält Spalten, die die Plattform nicht verwendet. Harmlos — sie werden einfach übersprungen.</td></tr>
            <tr><td><Code>... gap(s) detected in the time series</Code></td><td>Zwischen der ersten und letzten Zeile fehlen einige Stunden. Fügen Sie die fehlenden Stunden für beste Genauigkeit hinzu.</td></tr>
            <tr><td><Code>timestamp(s) are not in chronological order</Code></td><td>Die Zeilen sind nicht nach Zeit sortiert. Sortieren Sie die Datei nach der Zeitstempel-Spalte.</td></tr>
            <tr><td><Code>has ... missing value(s)</Code></td><td>Einige Zellen in einer Zahlenspalte sind leer. Füllen Sie diese aus (verwenden Sie <Code>0</Code>, wo angemessen).</td></tr>
            <tr><td><Code>... potential outliers</Code></td><td>Einige Werte sind ungewöhnlich hoch oder niedrig. Prüfen Sie diese auf Tippfehler; andernfalls können sie ignoriert werden.</td></tr>
            <tr><td><Code>contains all zero values</Code></td><td>Eine ganze Spalte ist null. Bei nächtlicher Solarenergie in Ordnung; andernfalls prüfen Sie, ob die richtigen Daten exportiert wurden.</td></tr>
          </tbody>
        </Table>
        <Tip>
          Die meisten roten Fehler entstehen durch das Dezimalkomma oder durch falsch geschriebene
          Überschriften. Wenn Sie von einer heruntergeladenen Vorlage ausgehen, vermeiden Sie
          beides — siehe <em>„CSV-Dateien korrekt erstellen"</em>.
        </Tip>
      </>
    ),
  },

  // =========================================================================
  {
    id: 'tb-forecast-flat',
    category: 'troubleshooting',
    title: 'Meine Prognose sieht flach oder falsch aus',
    summary: 'Wenn die vorhergesagte Linie eine Gerade, eine Kopie von gestern oder völlig falsch ist — was zu prüfen ist.',
    body: (
      <>
        <p>Wenn eine Prognose nicht hilfreich aussieht, liegt fast immer eine dieser Ursachen vor.</p>

        <Table bordered size="sm" responsive className="small">
          <thead style={{ background: 'var(--color-neutral-100)' }}>
            <tr><th>Was Sie sehen</th><th>Wahrscheinlichste Ursache</th><th>Was zu tun ist</th></tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Gerade Linie</strong> — die Vorhersage ist über den gesamten Horizont ein einzelner Wert.</td>
              <td>Sehr wenig Eingangshistorie, oder die Eingabe besteht meist aus Nullen bzw. einem einzigen wiederholten Wert.</td>
              <td>Laden Sie mehr Historie hoch (mindestens einen Monat, idealerweise ein Jahr). Prüfen Sie, ob die Datei keinen einzigen wiederholten Wert enthält.</td>
            </tr>
            <tr>
              <td><strong>„Kopie von gestern"</strong> — die Vorhersage sieht wie die letzten 24 Stunden, zeitlich verschoben, aus.</td>
              <td>Das Modell ist auf eine naive Basislinie zurückgefallen, weil die Daten zu kurz für ein echtes Modell sind.</td>
              <td>Fügen Sie mehr Historie hinzu. Fügen Sie eine Wetterdatei für Solarprognosen hinzu (siehe <ArticleLink to="data-weather">Wetterdateien</ArticleLink>).</td>
            </tr>
            <tr>
              <td><strong>Spitzen / negative Werte</strong> in der Vorhersage.</td>
              <td>Ausreißer in der Historie (z. B. ein Zählerfehler) beeinflussen das Modell.</td>
              <td>Öffnen Sie die Historiendatei, finden Sie die verdächtige Zeile, korrigieren oder entfernen Sie sie, laden Sie sie erneut hoch und führen Sie die Prognose erneut aus.</td>
            </tr>
            <tr>
              <td><strong>Sehr breites Konfidenzband</strong>.</td>
              <td>Das Modell weiß, dass es unsicher ist — meistens kurze Historie oder ungewöhnliche aktuelle Daten.</td>
              <td>Fügen Sie mehr Daten hinzu; das Band sollte sich beim nächsten Durchlauf verengen.</td>
            </tr>
            <tr>
              <td><strong>„Beispieldaten"-Hinweis auf der Seite</strong>.</td>
              <td>Der Prognosedienst war nicht verfügbar oder die Daten waren unzureichend — es werden illustrative Ergebnisse angezeigt.</td>
              <td>Prüfen Sie die Upload-Seite auf fehlende Dateien; versuchen Sie es in einigen Minuten erneut.</td>
            </tr>
            <tr>
              <td><strong>Solarprognose ist ungefähr richtig, aber die Größenordnung stimmt nicht</strong>.</td>
              <td>PV-Kapazität oder Ausrichtung der Paneele sind möglicherweise falsch eingestellt, oder die Erzeugungsdatei verwendet kWh, wo kW erwartet wird (oder umgekehrt).</td>
              <td>Überprüfen Sie die installierte Leistung des Assets (kWp). Vergewissern Sie sich, dass die Einheit der Erzeugungsdatei mit den Erwartungen der Plattform auf der Upload-Seite übereinstimmt.</td>
            </tr>
          </tbody>
        </Table>

        <Tip>
          Ein nützlicher Schnelltest: Wenn Sie die Prognose für einen <em>vergangenen</em> Tag eines
          Gebäudes erstellen und die Vorhersage überhaupt nicht dem entspricht, was tatsächlich
          passiert ist, ist das Modell unterversorgt. Mehr Historie hinzuzufügen ist fast immer
          effektiver als das Anpassen von Einstellungen.
        </Tip>
      </>
    ),
  },

  // =========================================================================
  {
    id: 'tb-optimisation-infeasible',
    category: 'troubleshooting',
    title: 'Optimierung meldet „infeasible"',
    summary: 'Wenn der Optimierungslauf keinen gültigen Fahrplan findet — häufige Ursachen und Lösungen.',
    body: (
      <>
        <p>
          „Infeasible" bedeutet, dass der Optimierer keinen Fahrplan finden konnte, der
          <em> alle</em> Nebenbedingungen gleichzeitig erfüllt. Meistens ist eine Nebenbedingung zu
          streng.
        </p>

        <h5 className="mt-3">Was zu prüfen ist, in dieser Reihenfolge</h5>
        <ol>
          <li>
            <strong>Batteriekapazität vs. Bedarf.</strong> Wenn Sie die Batterie gezwungen haben, den
            Abendspitzenbedarf zu decken, ihre Kapazität jedoch zu gering ist, hat der Optimierer
            keinen gültigen Plan. Erhöhen Sie die Batteriekapazität oder lockern Sie die
            Nebenbedingung.
          </li>
          <li>
            <strong>EV-Ladefenster vs. benötigte Energie.</strong> Wenn ein EV 40 kWh benötigt,
            aber nur für 2 Stunden bei 7 kW angeschlossen ist (= maximal 14 kWh), kann es nicht
            vollständig geladen werden. Erweitern Sie das Fenster oder reduzieren Sie den
            erforderlichen SOC.
          </li>
          <li>
            <strong>Autarkie-Ziel vs. Erzeugung.</strong> Ein Ziel von „100 % Autarkie" mit wenig
            PV und ohne erlaubten Netzbezug ist unmöglich. Senken Sie das Ziel oder erlauben Sie
            etwas Netzbezug.
          </li>
          <li>
            <strong>Min-/Max-SOC-Grenzen.</strong> Wenn der minimale SOC höher ist als der maximale
            SOC (oder beide auf demselben Niveau fixiert sind), gibt es keinen Spielraum zum
            Betrieb. Prüfen Sie beide Grenzen.
          </li>
          <li>
            <strong>Negative Tarifpreise.</strong> Einige Exporte zeigen in realen Märkten negative
            Großhandelspreise. Wenn Sie eine Regel „Export ≥ 0" festgelegt haben, werden diese
            Stunden nicht lösbar. Entfernen Sie die Regel oder filtern Sie die Tarifdatei vor.
          </li>
          <li>
            <strong>Fehlende Daten im Horizont.</strong> Eine Lücke in der Last- oder Tarifdatei
            innerhalb des Optimierungshorizonts kann den Solver zum Absturz bringen. Laden Sie die
            Datei erneut hoch, nachdem Sie die Lücke gefüllt haben (verwenden Sie den Wert der
            Vorststunde oder 0).
          </li>
        </ol>

        <h5 className="mt-3">Wie man schnell diagnostiziert</h5>
        <p>
          Führen Sie die Optimierung erneut mit dem Ziel <strong>„Kosten minimieren"</strong> und
          <em> ohne</em> festes Autarkie-/Eigenverbrauchsziel aus. Gelingt das, war Ihr Ziel die
          bindende Nebenbedingung — lockern Sie es. Schlägt es immer noch fehl, liegt die bindende
          Nebenbedingung bei der Anlagenkapazität oder einer Datenlücke.
        </p>
        <Tip>
          Das Halbieren des Horizonts (24 h → 12 h) gelingt manchmal, wenn 24 h nicht funktioniert.
          Das deutet auf einen bestimmten Zeitblock hin, in dem das Problem liegt — prüfen Sie
          diesen Block in Ihren Daten.
        </Tip>
      </>
    ),
  },

  // =========================================================================
  {
    id: 'tb-kpi-sanity-check',
    category: 'troubleshooting',
    title: 'Meine KPIs sehen zu gut aus — oder zu schlecht',
    summary: 'Wenn die Autarkie bei 99 % liegt, der NPV riesig ist oder die Amortisation unplausibel kurz ist — was zu prüfen ist.',
    body: (
      <>
        <p>
          Ein KPI, der „zu schön um wahr zu sein" aussieht, geht fast immer auf einen von wenigen
          Datenfehler zurück. Gehen Sie diese Prüfpunkte durch, bevor Sie das Ergebnis weitergeben.
        </p>

        <h5 className="mt-3">Wenn Autarkie oder Eigenverbrauch unplausibel hoch ist</h5>
        <ul>
          <li><strong>Verbrauchsdatei zu klein skaliert.</strong> kW-Werte in einer kWh-Spalte eingegeben (oder umgekehrt) lassen die Gemeinschaft im Vergleich zur PV-Anlage winzig erscheinen. Überprüfen Sie die Einheit auf der Upload-Seite.</li>
          <li><strong>Nur ein Teiljahr hochgeladen.</strong> Wenn Sie nur die Sommermonate hochgeladen haben, deckt die Solarenergie natürlich den Großteil des Bedarfs. Laden Sie ein vollständiges Jahr für einen repräsentativen Wert hoch.</li>
          <li><strong>Fehlende Gebäude.</strong> Nur eines von drei Gebäuden befindet sich auf der Plattform, sodass die Gemeinschaft kleiner und solar-gedeckter erscheint als in der Realität.</li>
        </ul>

        <h5 className="mt-3">Wenn NPV oder jährliche Einsparungen unplausibel groß sind</h5>
        <ul>
          <li><strong>Tarif in falschen Einheiten.</strong> €/MWh, das als €/kWh behandelt wird, bläst die Einsparungen um das 1000-Fache auf. Öffnen Sie die Tarifdatei — ein Preis von <Code>120</Code> sollte <Code>0.12</Code> sein, wenn er €/kWh darstellt.</li>
          <li><strong>Lebensdauer zu lang.</strong> Ein 30-Jahres-Horizont für eine 15-jährige Batterie überschätzt die Einsparungen. Passen Sie die Lebensdauer an die Anlage an.</li>
          <li><strong>Diskontsatz auf 0 % gesetzt.</strong> Kein Diskontieren bedeutet, dass zukünftige Einsparungen vollständig zählen — in der Regel unrealistisch. Verwenden Sie 3–6 %, sofern Ihr Finanzteam nichts anderes vorgibt.</li>
          <li><strong>Zu aggressive Inflationsannahme.</strong> Wenn Sie angenommen haben, dass die Strompreise für immer um 8 % pro Jahr steigen, explodieren die Einsparungen. Verwenden Sie einen nüchternen Wert (1–4 %).</li>
        </ul>

        <h5 className="mt-3">Wenn die Ergebnisse unplausibel schlecht aussehen</h5>
        <ul>
          <li><strong>Tarifdatei fehlt oder ist komplett null.</strong> Kein Preis = keine zählbaren Einsparungen. Laden Sie den Tarif hoch und führen Sie die Berechnung erneut aus.</li>
          <li><strong>PV-Kapazität bei 0 kWp belassen.</strong> Prüfen Sie die Anlage auf der Seite „Gemeinschaftsanlagen".</li>
          <li><strong>Falsche Batterieeffizienz.</strong> Ein Standardwert von 50 % würde dazu führen, dass die Hälfte jeder kWh „verloren geht" — prüfen Sie den Wirkungsgrad (Round-Trip-Effizienz) der Anlage.</li>
          <li><strong>Erzeugungsdatei in der falschen Spalte.</strong> Wenn Verbrauch und Erzeugung vertauscht wurden, sieht der Optimierer das Gebäude als nicht produzierend an.</li>
        </ul>

        <Tip>
          Ein nützlicher Plausibilitätscheck: Teilen Sie die jährlichen Einsparungen durch die
          jährliche Stromrechnung der Gemeinschaft. Liegt das Ergebnis über 50–70 %, ist wahrscheinlich
          etwas überschätzt. Reale Gemeinschafts-Energieprojekte reduzieren die Rechnungen typischerweise
          um 10–40 %.
        </Tip>
      </>
    ),
  },

  // =========================================================================
  {
    id: 'tb-faq',
    category: 'troubleshooting',
    title: 'Häufig gestellte Fragen',
    summary: 'Kurze Antworten auf häufige Fragen.',
    body: (
      <>
        <h5>Brauche ich Daten für ein ganzes Jahr?</h5>
        <p>Ein vollständiges Jahr (8.760 stündliche Zeilen) liefert die besten Ergebnisse, aber die
          Tools funktionieren auch mit weniger. Für zeitbasierte Dateien sind mindestens ein Tag
          (24 Zeilen) erforderlich.</p>

        <h5 className="mt-3">Meine Datei verwendet ein Semikolon ( ; ) zwischen den Werten — ist das ein Problem?</h5>
        <p>Nein. Die Plattform erkennt automatisch Kommas, Semikolons und Tabulatoren. Das
          Dezimaltrennzeichen innerhalb von Zahlen muss jedoch weiterhin ein Punkt sein.</p>

        <h5 className="mt-3">Kann ich denselben Dateityp erneut hochladen?</h5>
        <p>Ja — ein erneutes Hochladen ersetzt die vorherige Datei für diesen Typ (bei Day-Ahead-Tarifen
          wird die Datei jedes Tages separat nach Datum gespeichert).</p>

        <h5 className="mt-3">Die Ergebnisse sehen wie Beispieldaten aus — warum?</h5>
        <p>Wenn ein Berechnungsdienst vorübergehend nicht verfügbar ist oder die erforderlichen Daten
          noch nicht hochgeladen wurden, zeigt die Plattform klar gekennzeichnete illustrative
          Ergebnisse an, damit Sie die Funktionen trotzdem erkunden können. Laden Sie echte Daten hoch
          und führen Sie das Tool erneut aus, um echte Ergebnisse zu erhalten.</p>

        <h5 className="mt-3">Warum ist eine Schaltfläche ausgegraut?</h5>
        <p>Einige Konten sind schreibgeschützt (Betrachter-Rolle), und für einige Aktionen müssen
          zunächst Daten hochgeladen werden. Fahren Sie mit der Maus über die Schaltfläche oder lesen
          Sie die Seitenhinweise, um zu sehen, was erforderlich ist.</p>
      </>
    ),
  },
]
